import { action, observable, runInAction } from 'mobx'
import { PaginationConfig } from 'antd/lib/pagination'
import * as _ from 'lodash'
import { isMobile } from 'react-device-detect'

import { StoreExt } from '@utils/reactExt'
// import { IUserInfoDetails, IUserInfo } from '@protocol/user'
import {
  ICheckTransferableAccount,
  INotification,
  IPaymentRewardEvent,
  IReceivedPaymentRewardEvent,
  ISetUserGameOptions,
  ITransferableAccountInfo,
  IUserInfoDetails,
  IUserNick,
  IUserUseItem,
  Schema$doCheckDuplicateLogin,
  Schema$TransferableAccount,
  Schema$UserGameOption,
  Schema$UserItem,
} from '@protocol/user'
import * as Utils from '@utils/utils'
import { getTicketCountFromTicket, showToast, TOAST_TYPE } from '@utils/utils'
import { ACCOUNT_TYPE, Schema$SimpleClubNotice, Schema$UserMission, Schema$userTitleDef } from '@interface/common'
import {
  authStore,
  chattingStore,
  globalStore,
  matchupStore,
  routerStore,
  bettorStore,
  userPickStore,
} from '@store/index'
import menus from '@views/PrimaryLayout/menu'
import { message } from 'antd'
import { Schema$Product } from '@protocol/shop'
import { SettlementSupport } from '@store/authStore'
import { BET_TICKET_TYPE, DUPLICATE_REQUEST_EXCEPTION, MESSAGE_ALERT_TEXT1 } from '@constants/index'
import { BubbleTextType, BubbleType } from '@store/globalStore'
import {
  Schema$UserActivityRewardHistory,
  Schema$UserActivitySummary,
  Schema$UserActivityTrack,
} from '@interface/user-activity'
import { FAVORITE_USER_SAVE_MODE, FAVORITE_USER_SORT, GlobalStoreKey, USER_COMMENT_TYPE } from '@v2/constants/common'
import { ERR } from 'adv-protocol/src/ErrorCodes'
import { Schema$Bettor, Schema$FavoriteRegistrationAlarmSetting } from '@protocol/bettor'
import { getErrorText } from '@game/errtext'
import PopupUtils from '@utils/popup-utils'

export interface IUser {
  _id?: string
  account: string
  password?: string
  category?: string
  createdAt?: string
}

export type UserItem = Schema$UserItem
export type UserInfoDetails = IUserInfoDetails.Schema
export type UserItems = UserItem[]
export type UserNick = IUserNick.Schema
export type PaymentReward = IPaymentRewardEvent.Schema
export type TransferableAccount = Schema$TransferableAccount

export interface BettingCoupon {
  item: UserItem
  volume: number
}

export interface UserTitleDefMap {
  [id: string]: Schema$userTitleDef
}

export class UserStore extends StoreExt {
  /**
   * 加载用户列表时的loading
   *
   * @type {boolean}
   * @memberof UserStore
   */
  @observable
  getUsersloading = false

  @observable
  userDetails: UserInfoDetails

  @observable
  userCharacter = 0

  @observable
  userItems: UserItem[] = []

  @observable
  userGameOptions: Schema$UserGameOption = {}

  @observable
  bettingCoupons: { [itemCode: string]: BettingCoupon }

  @observable
  users: IUser[] = []

  /**
   * table pageIndex
   *
   * @type {number}
   * @memberof UserStore
   */
  @observable
  pageIndex = 1

  /**
   * table pageSize
   *
   * @type {number}
   * @memberof UserStore
   */
  @observable
  pageSize = 30

  /**
   * users total
   *
   * @type {number}
   * @memberof UserStore
   */
  @observable
  total = 0

  @observable
  nick: UserNick

  @observable
  notificationData: INotification.Schema = {
    unreadMessageCount: null,
    unQuickReadMessageCount: null,
    liveMatchCount: 0,
  }

  @observable
  missionList: Schema$UserMission[]

  settlementSupport?: SettlementSupport

  betTicketCount: number

  showTutorialPopup: boolean

  isShowDiaAdvertiseImage: boolean

  hasDiaBettingHistory: boolean

  hasCasinoLoginHistory: boolean

  @observable
  paymentReward: PaymentReward = null

  @observable
  firstJoinAtByCivalue: Date

  /** 매치업 충전하기 버튼 노출 여부. 서버(/user/info)가 판정한 값을 그대로 사용한다. */
  @observable
  isShowMatchupChargeButton = false

  @observable
  isUsageGuideVideoAutoTarget = false

  @observable
  myActivity: Schema$UserActivitySummary

  @observable
  myActivityHistoryTracks: Schema$UserActivityTrack[] = []

  @observable
  myActivityHistoryScore = 0

  @observable
  myActivityHistoryHasMore = false

  @observable
  myActivityRewardHistory: Schema$UserActivityRewardHistory

  userTitleDefMap: UserTitleDefMap = {}

  @observable
  cheerForTeamUserActivityScoreLastTime: Date = null

  favoriteUserSaveMode = FAVORITE_USER_SAVE_MODE.NONE

  @observable
  favoriteUsers: Schema$Bettor[] = []

  @observable
  favoriteUsersHasMore = false

  favoriteUsersOffset = 0

  favoriteBettorLimit = 20

  @observable
  favoriteUsersTotalCount = 0


  @action
  resetPaymentReward = () => {
    this.paymentReward = null
  }

  @action
  fetchPaymentReward = async () => {
    try {
      const result = await this.api.user.paymentRewardEvent({})
      runInAction(() => {
        this.paymentReward = { ...result }
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  receivedPaymentReward = async (rewardId) => {
    try {
      const params: IReceivedPaymentRewardEvent.Params = {
        rewardId,
      }

      const result = await this.api.user.receivedPaymentRewardEvent(params)
      if (result) {
        if (result.ok) {
          await this.fetchPaymentReward()
          Utils.showToast(Utils.TOAST_TYPE.INFO, '보상이 메시지 함으로 지급되었습니다.')
        } else {
          Utils.showToast(Utils.TOAST_TYPE.INFO, '보상 받기에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
        }
      } else {
        Utils.showToast(Utils.TOAST_TYPE.INFO, '보상 받기에 실패하였습니다. 새로고침 후 다시 시도해 주세요.')
      }
    } catch (err) {
      console.error(err)
    }
  }


  isRegisteredTelegram = () => {
    return _.isNumber(this.userGameOptions.telegramChatId)
  }

  @action
  cleanup = () => {
    this.getUsersloading = false
    this.userDetails = undefined
    this.userItems = []
    this.users = []
    this.pageIndex = 1
    this.pageSize = 30
    this.total = 0
    this.nick = undefined
  }

  @action
  fetchBettingCoupons = async () => {
    try {
      const { items, volumes } = await this.api.user.getBettingCoupons({})
      runInAction(() => {
        const info = {}
        for (const el of items) {
          info[el.item_def_id] = {
            item: el,
            volume: volumes[el.item_def_id] || 0,
          }
        }
        this.bettingCoupons = info
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * 加载用户列表
   *
   * @memberof UserStore
   */
  @action
  getUsers = async () => {
    this.getUsersloading = true
    try {
      const res = await this.api.user.getUsers({ pageIndex: this.pageIndex, pageSize: this.pageSize })
      runInAction('SET_USER_LIST', () => {
        this.users = res.users
        this.total = res.total
      })
      // eslint-disable-next-line no-empty
    } catch (err) {
    }
    runInAction('HIDE_USER_LIST_LOADING', () => {
      this.getUsersloading = false
    })
  }

  @action
  getUserDetails = async (params: IUserInfoDetails.Params) => {
    try {
      const res = await this.api.user.getUserInfoDetails(params)
      this.setUserDetails(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  getUserItems = async (propertyIds: number[] | null) => {
    try {
      const { character, items } = await this.api.user.getUserItems({ propertyIds })
      runInAction(() => {
        this.userCharacter = character
        this.userItems = items
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  userUseItem = async (params: IUserUseItem.Params) => {
    try {
      await this.api.user.useUserItem(params)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  changeProfileImageByCustomPhoto = async (imageName: string) => {
    try {
      await this.api.user.changeProfileImageByCustomPhoto({ imageName })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  resetProfileImage = async () => {
    try {
      await this.api.user.resetProfileImageUrl()
    } catch (err) {
      console.error(err)
    }
  }

  @action
  getUserGameOption = async (fetch = true) => {
    try {
      if (fetch) {
        const res = await this.api.user.getUserGameOptions({})
        if (res) {
          const { options } = res
          runInAction(() => {
            this.userGameOptions = options
          })
        }
      }

      return this.userGameOptions
    } catch (err) {
      console.error(err)
    }
    return null
  }

  @action
  setUserGameOption = async (params: ISetUserGameOptions.Params): Promise<ISetUserGameOptions.Schema> => {
    try {
      const result = await this.api.user.setUserGameOptions(params)
      if (result.result) {
        runInAction(() => {
          this.userGameOptions = { ...this.userGameOptions, ...params }
        })
      }
      return result
    } catch (err) {
      console.error(err)
    }

    return null
  }

  @action
  checkNick = async (params: IUserNick.Params) => {
    try {
      const res = await this.api.user.checkNick(params)
      this.setUserNick(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  changeNick = async (params: IUserNick.Params) => {
    try {
      const res = await this.api.user.changeNick(params)
      this.setUserNick(res)
    } catch (err) {
      console.error(err)
      Utils.showToast(Utils.TOAST_TYPE.INFO, '닉네임 변경 실패!  잠시후 다시 시도해주세요.')
    }
  }

  createUser = async (user: IUser) => {
    await this.api.user.createUser(user)
  }

  modifyUser = async (user: IUser) => {
    await this.api.user.modifyUser(user)
  }

  deleteUser = async (_id: string) => {
    await this.api.user.deleteUser({ _id })
    this.getUsers()
  }

  @action
  changePageIndex = (pageIndex: number) => {
    this.pageIndex = pageIndex
    this.getUsers()
  }

  @action
  changePageSize = (pageSize: number) => {
    this.pageSize = pageSize
    this.getUsers()
  }

  handleTableChange = (pagination: PaginationConfig) => {
    const { current, pageSize } = pagination
    if (current !== this.pageIndex) {
      this.changePageIndex(current)
    }
    if (pageSize !== this.pageSize) {
      this.changePageSize(pageSize)
    }
  }

  @action
  setUserDetails = (details: UserInfoDetails): UserInfoDetails => {
    this.userDetails = details
    return details
  }

  @action
  setUserNick = (nick: UserNick): UserNick => {
    this.nick = nick
    return nick
  }

  @action
  fetchNotificationData = async (params: INotification.Params) => {
    try {
      const res = await this.api.user.getNotificationData(params)
      const { error } = res
      if (!res || error === DUPLICATE_REQUEST_EXCEPTION) {
        return
      }
      this.setNotificationData(res)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setNotificationData = (notificationData: INotification.Schema): INotification.Schema => {
    this.notificationData = notificationData
    return notificationData
  }

  @action
  setLiveMatchCount(liveMatchCount: number) {
    this.notificationData = {
      ...this.notificationData,
      liveMatchCount,
    }
  }

  @action
  setUnQuickReadMessageCount(count: number) {
    this.notificationData.unQuickReadMessageCount = _.toNumber(count)
  }

  @action
  increaseUnQuickReadMessageCount(count: number | string) {
    if (_.isNil(this.notificationData?.unQuickReadMessageCount)) {
      this.setUnQuickReadMessageCount(0)
    }

    this.setUnQuickReadMessageCount(this.notificationData.unQuickReadMessageCount += _.toNumber(count))
  }

  @action
  decreaseUnQuickReadMessageCount(count = 1) {
    if (_.isNil(count)) {
      return
    }

    if (this.notificationData?.unQuickReadMessageCount > 0) {
      this.setUnQuickReadMessageCount(this.notificationData.unQuickReadMessageCount -= count)
    }
  }

  @action
  setUnreadMessageCount(count: number) {
    this.notificationData.unreadMessageCount = _.toNumber(count)
  }

  @action
  increaseUnreadMessageCount(count: number | string) {
    if (_.isNil(this.notificationData?.unreadMessageCount)) {
      this.setUnreadMessageCount(0)
    }

    this.notificationData.unreadMessageCount += _.toNumber(count)
  }

  @action
  decreaseUnReadMessageCount(count = 1) {
    if (_.isNil(count)) {
      return
    }

    if (this.notificationData.unreadMessageCount > 0 && this.notificationData.unreadMessageCount >= count) {
      this.notificationData.unreadMessageCount -= count
    }

    if (this.notificationData.unreadMessageCount === 0) {
      globalStore.showMessageAlertAniTimer()
      globalStore.showBubblePopupTimer(BubbleTextType.MESSAGE_ALERT, MESSAGE_ALERT_TEXT1, BubbleType.MESSAGE_ALERT)
    }
  }

  @action
  doCheckDuplicateLogin = async (doCheckDuplicateLogin: Schema$doCheckDuplicateLogin) => {
    try {
      await this.api.user.doCheckDuplicateLogin(doCheckDuplicateLogin)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  getUserMissionList = async (): Promise<void> => {
    try {
      const response = await this.api.user.getUserMissionList(null)
      const { error } = response
      if (!response || error === DUPLICATE_REQUEST_EXCEPTION) {
        return
      }
      this.setMissionList(response.missionList)
    } catch (error) {
      console.error(`getUserMissions error : ${error}`)
    }
  }

  showMissionCompleteModalAvailable = () => {
    if (_.isEmpty(this.missionList)) {
      return false
    }
    if (globalStore.visibleMissionCompletePopup) {
      return false
    }
    if (globalStore.visibleMissionListPopup) {
      return false
    }
    const notPickUpMissionReward = _.find(this.missionList, { missionComplete: true, rewardComplete: false })
    if (notPickUpMissionReward) {
      if (isMobile) {
        const pathName = routerStore.location.pathname
        const missionMenu = _.find(menus, { namespace: 'mission' })
        return pathName !== missionMenu.path
      }
      if (globalStore.currentGeneralPopup?.title !== '미션') {
        return true
      }
    }
    return false
  }

  rebuildingMissionList = (missionList: Schema$UserMission[]): Schema$UserMission[] => {
    const topList = _.filter(missionList, { missionComplete: true, rewardComplete: false })
    const midList = _.filter(missionList, { missionComplete: true, rewardComplete: true })
    const bottomList = _.filter(missionList, { missionComplete: false, rewardComplete: false })
    return [..._.sortBy(topList, ['defId']), ..._.sortBy([...midList, ...bottomList], ['defId'])]
  }

  @action
  setMissionList = (missionList: Schema$UserMission[]): void => {
    this.missionList = this.rebuildingMissionList(missionList)
    if (this.showMissionCompleteModalAvailable()) {
      globalStore.setVisibleMissionCompletePopup(true)
    }
  }

  @action
  pickUpMissionReward = async (mission: Schema$UserMission): Promise<void> => {
    const response = await this.api.user.pickUpMissionReward({ missionDefId: mission.defId })
    runInAction(() => {
      const existsMission = _.find(this.missionList, { id: mission.id })
      existsMission.rewardComplete = response.rewardedMission.rewardComplete
    })
    PopupUtils.showMessagePopup()
    message.info('미션 보상이 메세지 함으로 지급 되었습니다.')
  }

  @action
  pickUpAllMissionReward = async () => {
    const response = await this.api.user.pickUpAllMissionReward()
    runInAction(() => {
      const updatedMissionList = response.updatedMissionList
      for (const updatedMission of updatedMissionList) {
        const existsMission = _.find(this.missionList, { id: updatedMission.id })
        if (existsMission) {
          existsMission.rewardComplete = updatedMission.rewardComplete
          existsMission.missionComplete = updatedMission.missionComplete
        }
      }
      this.setMissionList(this.missionList)
    })
    PopupUtils.showMessagePopup()
    message.info('미션 보상이 메세지 함으로 지급 되었습니다.')
  }

  @action
  updateMissionCount = async (productItem: Schema$Product, count: number) => {
    const response = await this.api.user.updateMissionCount({ productItem, count })
    const existsMission = _.find(this.missionList, { id: response.id })
    if (existsMission) {
      existsMission.missionComplete = response.missionComplete
    }
    runInAction(() => {
      this.setMissionList(this.missionList)
    })
  }

  setGuidPopupDisplayed = async () => {
    await this.api.user.setGuidePopupDisabled()
  }

  recordUsageGuideVideoWatched = async () => {
    await this.api.user.usageGuideVideoWatched()
  }

  receiveGuideBonus = async () => {
    const result = await this.api.user.receiveGuideBonus()
    return result
  }

  setSettlementSupport = (settlementSupport: SettlementSupport): SettlementSupport => {
    this.settlementSupport = settlementSupport
    return settlementSupport
  }

  setShowTutorialPopup = (showTutorialPopup: boolean) => {
    this.showTutorialPopup = showTutorialPopup
    return showTutorialPopup
  }

  getTicketCount = (type: BET_TICKET_TYPE, ticket: number) => {
    this.betTicketCount = Utils.getTicketCountFromTicket(type, ticket)
    return this.betTicketCount
  }

  @action
  getOwnedFreeLicense = async () => {
    const result = await this.api.user.getOwnedFreeLicense()
    return result
  }

  setHasDiaBettingHistory = (hasDiaBettingHistory: boolean) => {
    this.hasDiaBettingHistory = hasDiaBettingHistory
  }

  setHasCasinoLoginHistory = (hasCasinoLoginHistory: boolean) => {
    this.hasCasinoLoginHistory = hasCasinoLoginHistory
  }

  @action
  setFirstJoinAtByCivalue = (firstJoinAt: Date) => {
    this.firstJoinAtByCivalue = firstJoinAt
  }

  @action
  setIsShowMatchupChargeButton = (isShowMatchupChargeButton: boolean) => {
    this.isShowMatchupChargeButton = isShowMatchupChargeButton ?? false
  }

  @action
  setIsUsageGuideVideoAutoTarget = (isUsageGuideVideoAutoTarget: boolean) => {
    this.isUsageGuideVideoAutoTarget = isUsageGuideVideoAutoTarget
  }

  documentVisibleChangeHandler(isHidden: boolean): void {
    if (!isMobile) {
      return
    }

    if (!authStore.isSignedIn) {
      return
    }

    if (isHidden) {
      return
    }

    const { club } = matchupStore
    if (_.isEmpty(_.trim(club?.id))) {
      return
    }
    this.fetchNotificationData({ clubId: club.id })

    if (authStore.accountInfo?.sign === ACCOUNT_TYPE.KAKAO) {
      const userPromiseId = authStore.userInfo.id
      this.showAccountTransferInfo(userPromiseId, ACCOUNT_TYPE.KAKAO).catch(console.error)
    }
  }

  @action
  async fetchMyActivity(baseMonth: string): Promise<Schema$UserActivitySummary> {
    try {
      const result = await this.api.my.fetchMyActivity({ baseMonth })

      runInAction(() => {
        this.myActivity = result.data
      })
      return result.data
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchMyActivityHistory(baseMonth: string, pageNum: number, limit: number): Promise<void> {
    try {
      const params = {
        baseMonth,
        pageNum,
        limit,
      }
      const result = await this.api.my.fetchMyActivityHistory(params)

      runInAction(() => {
        const tracks = result.data.tracks
        if (pageNum === 0) {
          this.resetMyActivityHistory()
        }
        if (!_.isEmpty(tracks?.[0])) {
          const activityDate = tracks[0].activityDate
          const existsTrack = _.find(this.myActivityHistoryTracks, { activityDate })
          let newTracks = []
          if (existsTrack) {
            existsTrack.activities = [].concat(existsTrack.activities).concat(tracks[0].activities)
            _.remove(tracks, { activityDate })
          }

          newTracks = this.myActivityHistoryTracks.concat(tracks)
          this.myActivityHistoryTracks = newTracks
        }
        this.myActivityHistoryScore = result.data.activityScore
        this.myActivityHistoryHasMore = result.data.hasMore
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  async fetchMyActivityRewardHistory(baseMonth: string): Promise<void> {
    try {
      const params = {
        baseMonth,
      }
      const result = await this.api.my.fetchMyActivityRewardHistory(params)
      this.setMyActivityRewardHistory(result.data)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setMyActivityRewardHistory(history: Schema$UserActivityRewardHistory): void {
    this.myActivityRewardHistory = history
  }

  @action
  resetMyActivityHistory(): void {
    this.myActivityHistoryHasMore = false
    this.myActivityHistoryTracks = []
    this.myActivityHistoryScore = 0
  }

  @action
  async receiveMyActivityReward(baseMonth: string, rewardDefId: number): Promise<void> {
    try {
      const result = await this.api.component.receiveUserActivityReward({
        baseMonth, activityRewardDefId: rewardDefId,
      })
      if (result.errMsg) {
        showToast(TOAST_TYPE.ERROR, result.errMsg)
      }
      runInAction(() => {
        if (result.result.rewardReceived && !_.isEmpty(this.myActivityRewardHistory?.rewardList)) {
          const exists = _.find(this.myActivityRewardHistory.rewardList, { rewardDefId: result.result.rewardDefId })
          if (exists) {
            exists.rewardReceived = true
            showToast(TOAST_TYPE.INFO, '보상 수령 완료. 메시지함을 확인하세요!')
          }
        }
        if (!_.isNil(result?.activityInfo)) {
          globalStore.setUserActivityInfo(result.activityInfo)
        }
      })
    } catch (err) {
      console.error(err)
    }
  }

  readClubNotice(clubNoticeList: Schema$SimpleClubNotice[]): Promise<void> {
    const userPromiseId = authStore.userInfo?.id || ''

    if (_.isEmpty(userPromiseId)
      || _.isEmpty(clubNoticeList)) {
      return
    }

    this.api.user.createClubNoticeReadHistory({ clubNoticeList }).then(response => {
      const { result } = response
      if (_.eq(result, 'ok')) {
        const clubNoticeIds = _.map(clubNoticeList, 'id')
        matchupStore.addClubNoticeReadHistory(clubNoticeIds)
      }
    }).catch(console.error)
  }

  @action
  decreaseMyActivityTotalCommentCount = (commentType: USER_COMMENT_TYPE): void => {
    if (!_.isNil(this.myActivity)) {
      this.myActivity.totalCommentCount -= 1
      switch (commentType) {
        case USER_COMMENT_TYPE.USER_PICK_COMMENT:
          this.myActivity.totalUserPickCommentCount -= 1
          break
        case USER_COMMENT_TYPE.COMPANY_PICK_COMMENT:
          this.myActivity.totalCompanyPickCommentCount -= 1
          break
      }
    }
  }

  @action
  decreaseMyActivityMonthlyCommentCount = (commentType: USER_COMMENT_TYPE): void => {
    if (!_.isNil(this.myActivity)) {
      this.myActivity.monthlyCommentCount -= 1
      switch (commentType) {
        case USER_COMMENT_TYPE.USER_PICK_COMMENT:
          this.myActivity.monthlyUserPickCommentCount -= 1
          break
        case USER_COMMENT_TYPE.COMPANY_PICK_COMMENT:
          this.myActivity.monthlyCompanyPickCommentCount -= 1
          break
      }
    }
  }

  @action
  decreaseMyActivityTotalCheerForTeam = (): void => {
    if (!_.isNil(this.myActivity)) {
      this.myActivity.totalCheerForTeamCount -= 1
    }
  }

  @action
  decreaseMyActivityMonthlyCheerForTeam = (): void => {
    if (!_.isNil(this.myActivity)) {
      this.myActivity.monthlyCheerForTeamCount -= 1
    }
  }

  fetchUserTitleDefs = async (): Promise<void> => {
    try {
      const result = await this.api.user.fetchUserTitleDefs()

      this.userTitleDefMap = {}
      result.defs.forEach(def => {
        this.userTitleDefMap[String(def.id)] = def
      })
    } catch (err) {
      console.error(err)
    }
  }

  /** 응원글 활동 점수 획득 마지막 일시. 채팅에서 응원글 매치 선택시 획득 가능 남은 시간 표시용 */
  @action
  setCheerForTeamUserActivityScoreLastTime(lastTime: Date): void {
    this.cheerForTeamUserActivityScoreLastTime = lastTime
  }

  async checkTransferableAccount(
    userPromiseId: string,
    accountType: ACCOUNT_TYPE,
  ): Promise<ICheckTransferableAccount.Schema> {
    try {
      return this.api.user.checkTransferableAccount({ userPromiseId, accountType })
    } catch (err) {
      console.error(err)
    }
  }

  async getTransferableAccountInfo(): Promise<ITransferableAccountInfo.Schema> {
    try {
      return this.api.user.getTransferableAccountInfo()
    } catch (err) {
      console.error(err)
    }
  }

  async registerAccountTransfer(targetUserPromiseId: string) {
    try {
      return this.api.user.registerAccountTransfer({ targetUserPromiseId })
    } catch (err) {
      console.error(err)
    }
  }

  async showAccountTransferInfo(
    userPromiseId: string,
    accountType: ACCOUNT_TYPE,
    callback?: () => Promise<void>,
  ): Promise<void> {
    try {
      if (globalStore.visibleAccountTransferPopup
        || (isMobile && chattingStore.isVisibleChattingModal)) {
        return
      }

      const isEnabled = globalStore.isAccountTransferEnabled()
      const isVisible = globalStore.isAccountTransferVisible()

      if (isEnabled && isVisible) {
        const { isTransferable, errcode } = await this.checkTransferableAccount(userPromiseId, accountType)

        if (!_.isNil(errcode)) {
          if (errcode === ERR.INVALID_ACCOUNT) {
            Utils.showToast(TOAST_TYPE.ERROR, '계정 정보가 올바르지 않습니다.')
          } else {
            Utils.showToast(TOAST_TYPE.ERROR, '요청에 실패하였습니다.')
          }

          return
        }

        if (isTransferable) {
          if (callback) {
            globalStore.setAccountTransferPopupCallback(callback)
          }

          globalStore.setVisibleAccountTransferPopup(true)
          return
        }
      }

      if (callback) {
        await callback()
      }
    } catch (err) {
      console.error(err)
    }
  }

  setFavoriteUserSaveMode = (mode: FAVORITE_USER_SAVE_MODE): void => {
    this.favoriteUserSaveMode = mode
  }

  @action
  favoriteUserState = (): void => {
    this.favoriteUsers = []
    this.favoriteUsersHasMore = false
    this.favoriteUsersOffset = 0
    this.favoriteBettorLimit = 20
    this.favoriteUsersTotalCount = 0
    this.favoriteUserSaveMode = FAVORITE_USER_SAVE_MODE.NONE
  }

  @action
  fetchFavoriteUsers = async (params: { sort: FAVORITE_USER_SORT, reset: boolean }): Promise<void> => {
    try {
      const { sort, reset = true } = params
      if (reset) {
        this.favoriteUserState()
      }
      const { items, hasMore, totalCount } = await this.api.user.fetchFavoriteUsers({
        sort,
        offset: this.favoriteUsersOffset,
      })

      if (this.favoriteUserSaveMode === FAVORITE_USER_SAVE_MODE.UNREGISTER_ALL) {
        _.forEach(items, item => {
          item.isFavoriteUser = false
        })
      }

      runInAction(() => {
        this.favoriteUsers = this.favoriteUsers.concat(items)
        this.favoriteUsersHasMore = hasMore
        this.favoriteUsersOffset += _.size(items)
        this.favoriteUsersTotalCount = totalCount
      })
    } catch (err) {
      message.error(err.message)
    }
  }

  setAllUserFavorite(isFavorite: boolean): void {
    runInAction(() => {
      this.favoriteUsers = this.favoriteUsers.map(bettor => {
        if (bettor.isFavoriteUser !== isFavorite) {
          return { ...bettor, isFavoriteUser: isFavorite }
        }
        return bettor
      })
    })
  }

  setUserFavorite = (params: { userPromiseIds: string[], isFavorite: boolean }): void => {
    const { userPromiseIds, isFavorite } = params
    runInAction(() => {
      const userIdSet = new Set(userPromiseIds)
      this.favoriteUsers = this.favoriteUsers.map(bettor => {
        if (userIdSet.has(bettor.id)) {
          return { ...bettor, isFavoriteUser: isFavorite }
        }
        return bettor
      })
    })
  }

  setUserLastMessageSentAt = (params: { userPromiseId: string, lastMessageSentAt: Date }): void => {
    const { userPromiseId, lastMessageSentAt } = params
    runInAction(() => {
      this.favoriteUsers = this.favoriteUsers.map(user => {
        if (user.id === userPromiseId) {
          return { ...user, lastMessageSentAt }
        }
        return user
      })
    })
  }

  showErrorMessage = (errcode: number): void => {
    let errText = ''
    if (errcode === ERR.ADD_FAVORITE_USER_LIMIT_EXCEEDED) {
      const limitCount = _.toNumber(globalStore.findGlobalSetting(GlobalStoreKey.FAVORITE_USER_COUNT_LIMIT)?.value ?? 0)
      if (limitCount < 1) {
        errText = '관심 등록할 수 없습니다.'
      } else {
        errText = `${limitCount}명까지 관심 등록할 수 있습니다.`
      }
    } else if(errcode === ERR.INVALID_PARAMETER) {
      errText = '관심 등록할 수 없습니다.'
    } else {
      errText = getErrorText(errcode)
    }
    showToast(TOAST_TYPE.ERROR, errText)
  }

  @action
  addFavoriteUsers = async (
    params: {
      userPromiseIds: string[],
      favoriteRegistrationAlarmUserPromiseId?: string,
    },
  ): Promise<boolean> => {
    try {
      const { errcode } = await this.api.user.addFavoriteUsers(params)
      if (!_.isNil(errcode)) {
        this.showErrorMessage(errcode)
        return false
      }
      const { userPromiseIds } = params
      this.setUserFavorite({ userPromiseIds, isFavorite: true })
      bettorStore.setBettorFavorite({ userPromiseIds, isFavorite: true })
      userPickStore.setUserPickRecentMatchPackageUserFavorite({ userPromiseIds, isFavorite: true })

      showToast(TOAST_TYPE.INFO, '관심 등록되었습니다.')
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  }

  getFavoriteAlarmSetting = async (): Promise<Schema$FavoriteRegistrationAlarmSetting> => {
    try {
      const { errcode, setting } = await this.api.user.getFavoriteAlarmSetting()
      if (!_.isNil(errcode)) {
        this.showErrorMessage(errcode)
        return null
      }
      return setting || { enabled: false, content: '', attachedUrl: '', attachedImage: '' }
    } catch (err) {
      console.error(err)
      return null
    }
  }

  saveFavoriteAlarmSetting = async (formData: FormData): Promise<boolean> => {
    try {
      const { errcode } = await this.api.user.saveFavoriteAlarmSetting(formData)
      if (!_.isNil(errcode)) {
        this.showErrorMessage(errcode)
        return false
      }
      return true
    } catch (err) {
      console.error(err)
      return false
    }
  }

  @action
  addAllFavoriteUsers = async () => {
    try {
      const userPromiseIds = _.chain(this.favoriteUsers)
        .filter(user => !user.isFavoriteUser).map('id').value()
      await this.api.user.addAllFavoriteUsers({ withoutUserPromiseIds: userPromiseIds })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  removeAllFavoriteUsers = async () => {
    try {
      const userPromiseIds = _.chain(this.favoriteUsers)
        .filter(user => user.isFavoriteUser).map('id').value()
      await this.api.user.removeAllFavoriteUsers({ withoutUserPromiseIds: userPromiseIds })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  removeFavoriteUsers = async (params: { userPromiseIds: string[] }) => {
    try {
      await this.api.user.removeFavoriteUsers(params)
      const { userPromiseIds } = params
      this.setUserFavorite({ userPromiseIds, isFavorite: false })
      bettorStore.setBettorFavorite({ userPromiseIds, isFavorite: false })
      userPickStore.setUserPickRecentMatchPackageUserFavorite({ userPromiseIds, isFavorite: false })
      showToast(TOAST_TYPE.INFO, '관심 해제되었습니다.')
    } catch (err) {
      console.error(err)
    }
  }

  checkHeartSendable = (): boolean => {
    let isHeartSendable = false
    const config = globalStore.findGlobalSetting(GlobalStoreKey.GIFT_HEART_ON_OFF)
    if (config) {
      isHeartSendable = config.value !== '0'
    }
    if (isHeartSendable) {
      isHeartSendable = authStore.userInfo.heart > 0
    }
    return isHeartSendable
  }

  checkTicketSendable = (): boolean => {
    let isTicketSendable = false
    const config = globalStore.findGlobalSetting(GlobalStoreKey.GIFT_TICKET_ON_OFF)
    if (config) {
      isTicketSendable = config.value !== '0'
    }
    if (isTicketSendable) {
      isTicketSendable = getTicketCountFromTicket(BET_TICKET_TYPE.BET_TICKET1, authStore.userInfo.ticket) > 0
    }
    return isTicketSendable
  }
}

export default new UserStore()
